/**
 * The landing page's state machine, kept free of the DOM so it can be tested
 * directly.
 *
 * The page is a list of people. Every row says what it will do, so there is no
 * button to describe and no sentence describing one: today's code, then the
 * games waiting on you, then everybody there is to play.
 *
 * What the player picked lives in localStorage, so the second visit is a
 * single tap; their name lives on the server, because an opponent has to be
 * able to see it.
 */

import { parsePeg, pegClasses } from './pegs.js';
import { restore, persist } from './storage.js';
import { createHold } from './hold.js';

/** localStorage keys. Namespaced so they cannot collide on a shared origin. */
export const MODE_KEY = 'unkoad.mode';

/**
 * Every switch there is, in the order the sheet draws them.
 *
 * Shared schema with models.Pings on the server: these strings are what a
 * PATCH names, and what the mailbox stores its answers under.
 */
export const PINGS = ['turns', 'nudges', 'daily', 'field'];

/** The sheet with nothing on, which is also what having no mailbox means. */
const NO_PINGS = { turns: false, nudges: false, daily: false, field: false };

/**
 * The lamp on a switch: one whole class list per state.
 *
 * Written out in full rather than assembled from the state's name. A class put
 * together at runtime is one Tailwind never sees in the source, so the rule is
 * dropped from the stylesheet - the build succeeds, the tests pass, and the
 * sheet draws four identical grey pegs.
 *
 * Red is the lamp the board says "Game over." in, which has never meant on
 * anywhere in the game - so a lit red switch cannot be read as a ping that is
 * now firing.
 */
const PING_LAMPS = {
    on: 'peg peg--solid',
    off: 'peg',
    trying: 'peg peg--solid peg--trying',
    failed: 'peg peg--solid peg--over',
};

/*
 * How wide a lamp can be drawn so that many of them still fit one line on the
 * narrowest phone. Keyed by how many are in the row, because that is the only
 * thing the width depends on.
 */
const PEG_SIZES = { 4: 1.75, 7: 1.35, 10: 1.05 };

/** What an unforeseen row falls back to: the size that always fits. */
const SMALLEST_PEG = 1.05;

export function createHome({
    name = '',
    modes = [],
    mode = '',
    // How this player's go at today's code stands, so the card can say where
    // they came without the page asking the server again.
    daily = null,
    storage = null,
    fetchImpl = null,
    cue = () => {},
    sound = null,
    notify = null,
} = {}) {
    const values = modes.map((m) => m.value);

    // The press held on a game's line. The board holds one of these too, for
    // the hold that pins a peg - same gesture, same threshold, one definition.
    const hold = createHold();

    return {
        modes,
        name,
        // What the server last confirmed it holds, so a name is only ever sent
        // when it has actually changed.
        savedName: name,
        // Falling back to the first mode makes the easiest one the default,
        // which is what a first-time player should meet.
        mode: restore(storage, MODE_KEY, values, mode || values[0] || ''),

        /** Which control is open: 'name', 'mode', or nothing. */
        open: '',
        /** Today's card, once this player has finished with today's code. */
        daily,
        /** The game whose nudge is in flight, so it cannot be sent twice. */
        reminding: '',
        /** The games already nudged in this visit. */
        nudged: [],
        /** The game the player is being asked to confirm giving up, if any. */
        forfeiting: '',
        /** The forfeit in flight, so the button cannot be pressed twice. */
        forfeitPending: false,
        /** The name being typed, kept apart until it is committed. */
        draft: '',
        saving: false,
        /** The save in flight, so two of them cannot overlap. */
        pending: null,
        storage,
        fetchImpl,
        /** How the page answers back. A no-op where no channel can reach. */
        cue,

        /**
         * The fetch this component sends with, or nothing where there is none.
         * Every request here goes through it, so a test stands one in once.
         */
        sender() {
            return this.fetchImpl || (typeof fetch === 'function' ? fetch : null);
        },

        /*
         * The mute.
         *
         * It lives here because the home page is the one screen a player passes
         * through on the way to everything else, and because a control for a
         * whole-app preference does not belong on a board where it would be one
         * more thing to hit by accident mid-game.
         *
         * Mirrored into the component rather than read from the channel on
         * every render: Alpine tracks what it can see, and the channel is
         * outside it, so a getter would set the icon once and never change it.
         */
        soundAvailable: Boolean(sound && sound.available()),
        muted: Boolean(sound && sound.muted()),

        /*
         * The notification switch, beside the mute for the same reason the
         * mute is here: home is the screen a player passes through on the way
         * to everything, which makes it the settings page a game this small
         * never grew. The boards only ever offer to turn notifications on;
         * this is the one place they are turned off, and now the only place
         * they are turned down.
         *
         * Mirrored like `muted`: the truth lives in the browser's permission
         * and subscription, and the component keeps a copy Alpine can see.
         */
        notifyState: 'unsupported',
        /*
         * The sheet.
         *
         * Which pings are wanted is the server's answer, not the browser's:
         * the server is what decides whether to send, and a second copy here
         * would only learn to disagree with it. Read when the sheet opens and
         * only then - most visits to home never open it, and this is a
         * document read somebody should have asked for.
         */
        wants: { ...NO_PINGS },
        /** Which switch is in flight: one of PINGS, 'all', or nothing. */
        pingBusy: '',
        /** Which switch the last attempt left red. */
        pingFailed: '',
        installHelp: false,
        /*
         * Whether the note about a browser that has refused is unfolded. Not
         * unfolded by the refusal itself: saying no is an answer, and meeting
         * it with instructions for taking it back would be arguing. It waits
         * for the player to ask, which is a tap on a switch that would
         * otherwise do nothing at all.
         */
        blockedHelp: false,

        init() {
            this.refreshNotify();
        },

        async refreshNotify() {
            if (!notify) return;
            this.notifyState = await notify.status();
            // A subscription the server lost - a failed save, a rotation the
            // worker slept through - heals on the next visit here.
            if (this.notifyState === 'on') notify.resync();
        },

        /** Whether the bell is worth drawing at all. */
        get notifyAvailable() {
            return this.notifyState !== 'unsupported';
        },

        /**
         * What the word says, which is also what it is called - the words are
         * its label. 'off' covers needs-install, because it is off and the
         * sheet it opens says what to do about that. A browser that has
         * refused is the one state that is neither: nothing in the sheet can
         * turn it on, so the word says which of the two it is.
         */
        get notifyLabel() {
            if (this.notifyState === 'on') return 'pings on';
            if (this.notifyState === 'blocked') return 'pings blocked';
            return 'pings off';
        },

        /*
         * The pings sheet.
         *
         * Four switches rather than one, because two of the nine notifications
         * this game sends are no longer somebody waiting on you: the day's code
         * arrives on a clock, and the field moves because of somebody else's
         * game. One switch for all of them was a fair bargain while every ping
         * was a move; it is not one now.
         *
         * The lamp on a row is the switch and the report both - off, on,
         * breathing while the flow runs, red when it did not take. There is no
         * spinner beside it and no sentence under it, because there is nothing
         * left for either to say.
         */

        /** Whether every switch is on, which decides what the last row offers. */
        get allPingsOn() {
            return this.notifyState === 'on' && PINGS.every((ping) => this.wants[ping]);
        },

        /** Whether anything at all would fire. */
        get anyPingOn() {
            return this.notifyState === 'on' && PINGS.some((ping) => this.wants[ping]);
        },

        /**
         * What the last row says, which is also all it does. Only ever one of
         * the two: with anything off the useful gesture is turning the rest on,
         * and turning them all off is only worth offering once there is
         * everything to turn off.
         */
        get allPingsLabel() {
            return this.allPingsOn ? 'Turn them all off' : 'Turn them all on';
        },

        /** Which of the four states a row's lamp is in. */
        pingStatus(ping) {
            if (this.pingFailed === ping || this.pingFailed === 'all') return 'failed';
            if (this.pingBusy === ping || this.pingBusy === 'all') return 'trying';
            return this.wants[ping] ? 'on' : 'off';
        },

        /** That state as the lamp's whole class list. */
        pingLamp(ping) {
            return PING_LAMPS[this.pingStatus(ping)];
        },

        /**
         * Reads the switches back, with every lamp breathing while it waits.
         *
         * The pulse costs nothing on a read that lands quickly: it is a
         * quarter second late by the stylesheet, which is longer than this
         * usually takes.
         */
        async loadWants() {
            if (this.pingBusy) return;
            this.pingBusy = 'all';
            try {
                await this.readWants();
            } finally {
                this.pingBusy = '';
            }
        },

        /**
         * The read itself, without touching what is in flight - it is also run
         * partway through a switch that is already breathing.
         *
         * A browser with no subscription has nothing to read: no mailbox means
         * nothing can fire, so every switch is off whatever defaults the
         * server would hand a mailbox it has not been asked for yet.
         */
        async readWants() {
            if (this.notifyState !== 'on') {
                this.wants = { ...NO_PINGS };
                return;
            }

            const send = this.sender();
            if (!send) return;

            try {
                const res = await send('/push');
                if (!res.ok) return;
                const body = await res.json();
                this.wants = { ...NO_PINGS, ...(body && body.wants) };
            } catch {
                // The lamps stay as they were. Tapping one is still the way to
                // set it, and a tap is a write rather than another read.
            }
        },

        /**
         * Turns one ping on or off.
         *
         * A switch is only a preference while there is a mailbox to hold it,
         * so setting one with notifications off turns them on first. The row
         * says what it will do, and that is the whole of what it does.
         */
        async setPing(ping, on) {
            if (this.pingBusy || !PINGS.includes(ping)) return;

            // The red is about the attempt that just ran, so it goes when the
            // next one starts rather than lingering over its outcome.
            this.pingFailed = '';
            this.pingBusy = ping;
            try {
                if (!(await this.mailboxReady(ping))) return;

                const filed = await this.putWants({ [ping]: on });
                if (!filed.ok) {
                    // The mailbox went while the sheet was open. Saying so
                    // here is what lets the same tap fix it: the next one
                    // finds notifications off and turns them on first.
                    if (filed.gone) this.notifyState = 'off';
                    this.pingFailed = ping;
                    return;
                }
                this.wants = { ...this.wants, [ping]: on };
                this.cue('peg');

                // The last switch off leaves a mailbox nothing will ever be
                // sent to and a permission the browser has no use for. Off
                // means off.
                if (!this.anyPingOn) await this.dropMailbox();
            } finally {
                this.pingBusy = '';
            }
        },

        /**
         * Turns the lot on, or the lot off.
         *
         * Off is the subscription itself going, not four switches being
         * written: a mailbox with nothing turned on is one the server would
         * never knock at, and leaving it there would leave the word in the
         * header saying pings are on.
         */
        async toggleAllPings() {
            if (this.pingBusy) return;

            const on = !this.allPingsOn;
            this.pingFailed = '';
            this.pingBusy = 'all';
            try {
                if (!on) {
                    await this.dropMailbox();
                    this.cue('peg');
                    return;
                }

                if (!(await this.mailboxReady('all'))) return;

                const wants = {};
                for (const ping of PINGS) wants[ping] = true;
                const filed = await this.putWants(wants);
                if (!filed.ok) {
                    if (filed.gone) this.notifyState = 'off';
                    this.pingFailed = 'all';
                    return;
                }
                this.wants = wants;
                this.cue('peg');
            } finally {
                this.pingBusy = '';
            }
        },

        /**
         * Makes sure there is a mailbox to file a switch against, turning
         * notifications on if there is not. Answers whether there is one.
         *
         * Every way this can fail leaves the lamp red. A browser that has
         * refused and an iPhone that wants the app on its home screen first
         * are answers rather than errors, so each also unfolds the note that
         * says what to do about it - but the lamp still reports the one thing
         * it was asked, which is that the switch did not move.
         */
        async mailboxReady(which) {
            if (this.notifyState === 'on') return true;
            if (!notify) {
                this.pingFailed = which;
                return false;
            }

            const result = await notify.enable();
            // 'failed' is the browser honestly off: a subscription was minted
            // and the server did not take it, so nothing was kept.
            this.notifyState = result === 'failed' ? 'off' : result;

            if (result === 'on') {
                // A new mailbox carries the server's defaults, which is not
                // something this page could have known.
                await this.readWants();
                return true;
            }

            this.installHelp = result === 'needs-install';
            this.blockedHelp = result === 'blocked';
            this.pingFailed = which;
            return false;
        },

        /**
         * Files switches with the server, answering whether they landed - and,
         * when they did not, whether the mailbox they were meant for is gone.
         * A switch with nothing to hold it is the one failure the next tap can
         * actually fix.
         */
        async putWants(wants) {
            const send = this.sender();
            if (!send) return { ok: false, gone: false };

            try {
                const res = await send('/push', {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ wants }),
                });
                return { ok: res.ok, gone: res.status === 409 };
            } catch {
                return { ok: false, gone: false };
            }
        },

        /** Lets the subscription go, once there is nothing left to send to it. */
        async dropMailbox() {
            this.wants = { ...NO_PINGS };
            if (!notify) return;
            this.notifyState = await notify.disable();
        },

        /** The chosen mode's row, or an empty one so getters stay safe. */
        get modeOption() {
            return this.modes.find((m) => m.value === this.mode)
                || { value: this.mode, number: 0, label: this.mode, codes: '' };
        },

        get modeLabel() {
            return this.modeOption.label;
        },

        /*
         * The picker draws real pegs, so it needs the same three readers the
         * board uses. They come from pegs.js rather than being reimplemented,
         * which is what keeps a preview peg and a played peg the same object.
         */
        pegClass(code) {
            return pegClasses(code);
        },

        pegColor(code) {
            return parsePeg(code).color;
        },

        pegDigit(code) {
            return parsePeg(code).digit;
        },

        /**
         * How big to draw one row of the picker's lamps, as a style.
         *
         * Taken from how many are in the row rather than from which mode they
         * belong to, so that Seventy's two rows - seven colours and ten digits -
         * come out at the sizes the Seven and Ten rows above them are drawn at.
         * The alphabets then line up as one column instead of four widths.
         */
        pegSize(count) {
            return `--peg-size: ${PEG_SIZES[count] || SMALLEST_PEG}rem`;
        },

        /*
         * The nudge.
         *
         * Offered on a game that has sat still for days, and held to one a day
         * by the server - this only stops the same button being pressed twice
         * on one visit, which is the part a page can honestly answer for.
         */
        reminded(id) {
            return this.nudged.includes(id);
        },

        /**
         * What the button says, which is also the only thing that reports the
         * nudge landed: it used to be a sentence on a strip above, and a strip
         * that exists to say "been a while" is saying what the button under it
         * already said.
         */
        remindLabel(id) {
            return this.reminded(id) ? 'NUDGED' : 'NUDGE';
        },

        async remind(id) {
            if (!id || this.reminding || this.reminded(id)) return;

            const send = this.sender();
            if (!send) return;

            this.reminding = id;
            try {
                const res = await send(`/g/${id}/remind`, { method: 'POST' });
                // A refusal is an answer too: the server holds the nudge to one
                // a day, and the line says the same thing either way rather
                // than arguing about which of the two it was.
                this.nudged = [...this.nudged, id];
                if (res.ok) this.cue('peg');
            } catch {
                // Nothing was sent, so the button stays pressable.
            } finally {
                this.reminding = '';
            }
        },

        /** Opens a control, or closes it if it was already the open one. */
        toggle(panel) {
            if (this.open === panel) {
                this.close();
                return;
            }
            if (this.open === 'name') this.applyDraft();
            // Opening the name control takes a copy to type into, so abandoning
            // the edit leaves the real name untouched.
            if (panel === 'name') this.draft = this.name;
            // The switches are the server's. Read on the way in rather than on
            // every visit to the page, and not awaited: the sheet is already
            // on screen, with its lamps breathing until the answer lands.
            if (panel === 'pings') this.loadWants();
            this.open = panel;
        },

        /**
         * Closes whatever is open, keeping a name that was being typed.
         *
         * Tapping away from a half-typed name is how somebody moves on, not how
         * they take it back - throwing the name away there would be a small
         * theft. Escape is the deliberate way out, and it is the only one.
         */
        close() {
            if (this.open === 'name') {
                this.applyDraft();
                this.open = '';
                // Not awaited: the name is already on screen, and play() waits
                // for this before it starts a game.
                this.saveName();
                return;
            }
            this.open = '';
        },

        /**
         * Moves what was typed into the name. An empty draft is a cancellation
         * rather than an erasure: the page always shows a name.
         */
        applyDraft() {
            const typed = this.draft.trim();
            if (typed) this.name = typed;
        },

        /*
         * Giving up a game.
         *
         * It lives on the game's own line in the list rather than on the board,
         * because giving up is something you do to a game you are done with,
         * not something you should meet every time you sit down to play one.
         * A press held on the line is what asks; the confirmation is what acts.
         */

        /** Starts the hold that offers to give a game up. */
        startPress(id) {
            hold.start(() => {
                this.forfeiting = id;
                // The threshold is the one part of a hold that has nothing to
                // show for itself, so it is the part most worth feeling.
                this.cue('hold');
            });
        },

        /** Ends the hold early: a tap, a scroll, or a finger leaving the line. */
        clearPress() {
            hold.cancel();
        },

        /**
         * Swallows the click that follows a completed hold, so opening the
         * panel does not also open the game behind it.
         */
        pressClick(event) {
            hold.click(event);
        },

        /** Closes the panel with the game left alone. */
        cancelForfeit() {
            this.forfeiting = '';
        },

        /**
         * Gives the game up. The server appends the forfeit row and settles who
         * won; the page is reloaded rather than patched, because the list it
         * renders came from the server in the first place.
         */
        async confirmForfeit() {
            const id = this.forfeiting;
            if (!id || this.forfeitPending) return;

            const send = this.sender();
            if (!send) return;

            // Felt on the press rather than on the answer: the reload that
            // follows a successful forfeit tears the page down, and a browser
            // stops the motor when it does. This is the moment anyway - giving
            // up is the decision, and the round-trip is the paperwork.
            this.cue('forfeit');

            this.forfeitPending = true;
            try {
                const res = await send(`/g/${id}/forfeit`, { method: 'POST' });
                if (res.ok) this.reload();
            } catch {
                // Nothing was given up, so leaving the panel open is the honest
                // outcome: the button can simply be pressed again.
            } finally {
                this.forfeitPending = false;
            }
        },

        /** Separated out so a test can watch for it instead of navigating. */
        reload() {
            if (globalThis.location && globalThis.location.reload) globalThis.location.reload();
        },

        /** Picks a mode and remembers it for next time. */
        choose(value) {
            if (!values.includes(value)) return;
            this.mode = value;
            persist(this.storage, MODE_KEY, value);
            this.cue('peg');
            this.close();
        },

        /**
         * Turns the sound off, or back on.
         *
         * The cue fires after the change, which is what makes the button
         * demonstrate itself: unmuting is heard, muting is only felt. Saying
         * "sound is now on" with silence would be the one case where this
         * control lies about what it did.
         */
        toggleSound() {
            if (!this.soundAvailable) return;
            this.muted = !this.muted;
            sound.mute(this.muted);
            this.cue('peg');
        },

        /** Accepts what was typed into the name control. */
        commitName() {
            this.applyDraft();
            this.open = '';
            return this.saveName();
        },

        /** Abandons the edit, which is the one way to discard what was typed. */
        cancelName() {
            this.draft = this.name;
            this.open = '';
        },

        /**
         * Sends the name to the server, which is the only copy that matters -
         * an opponent reads it from the game, not from this device.
         *
         * The server's answer is authoritative, since it trims and truncates
         * what it was given.
         */
        async saveName() {
            // Join a save already under way rather than racing it, then look
            // again: the name may have changed while that one was in flight.
            if (this.pending) await this.pending;
            if (this.name === this.savedName) return;

            const send = this.sender();
            if (!send) return;

            this.saving = true;
            this.pending = this.sendName(send, this.name);
            try {
                await this.pending;
            } finally {
                this.pending = null;
                this.saving = false;
            }
        },

        async sendName(send, name) {
            try {
                const res = await send('/name', {
                    method: 'POST',
                    // Outlives the page: solo leaves without waiting for it.
                    keepalive: true,
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                    body: new URLSearchParams({ name }).toString(),
                });
                if (!res.ok) {
                    // Put the confirmed name back, so the page never claims a
                    // name the server did not take.
                    this.name = this.savedName;
                    return;
                }
                const body = await res.json();
                if (body && body.name) this.name = body.name;
                this.savedName = this.name;
            } catch {
                this.name = this.savedName;
            }
        },

        /**
         * Starts solo, sending a name still being typed on the way out. Solo
         * does not use the name, and must not wait on the network for it.
         */
        async play(start) {
            if (this.open === 'name') this.applyDraft();
            this.open = '';
            const saved = this.saveName();
            if (typeof start === 'function') start();
            await saved;
        },
    };
}
